<template>
  <div>
    <v-card flat>
      <v-card-title class="text-h5 font-weight-bold">
        {{ $t('Booking Settings') }}
      </v-card-title>

      <v-card-text>
        <v-form ref="form">
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-4 mb-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Reservation Settings') }}
                </v-card-title>

                <v-text-field
                  v-model.number="formData.min_ahead"
                  dense
                  outlined
                  type="number"
                  min="0"
                  :rules="[required, minZero]"
                  :label="$t('Minimum Days Ahead')"
                  @input="updateForm"
                />

                <v-select
                  v-model="formData.default_sort"
                  dense
                  outlined
                  :items="sortOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('Default Sort')"
                  @change="updateForm"
                />

                <v-switch
                  v-model="formData.show_infants"
                  class="mt-0"
                  size="small"
                  :label="$t('Show infants count picker')"
                  @change="updateForm"
                />
                <v-text-field
                  v-model="formData.default_rate_plan_title"
                  dense
                  outlined
                  :label="$t('Default Rate Plan Title')"
                  @input="updateForm"
                />
              </v-card>

              <v-card outlined class="pa-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Payment Settings') }}
                </v-card-title>

                <v-checkbox
                  v-model="formData.allow_bank_payments"
                  :label="$t('Allow bank transfers')"
                  @change="updateForm"
                />

                <div class="d-flex align-center mb-4">
                  <v-checkbox
                    v-model="formData.include_cleaning_in_nightly_rate"
                    class="ma-0 pa-0 mr-2"
                    hide-details
                    :label="$t('Hide cleaning fee')"
                    @change="updateForm"
                  />
                  <info-tooltip
                    :text="
                      $t(
                        'When enabled, the cleaning fee will be added to the nightly rate and hidden from the guest breakdown.'
                      )
                    "
                  />
                </div>

                <div class="d-flex align-center mb-4">
                  <v-checkbox
                    v-model="formData.compare_prices"
                    class="ma-0 pa-0 mr-2"
                    hide-details
                    :label="$t('Compare prices with Airbnb')"
                    @change="updateForm"
                  />
                  <info-tooltip
                    :text="
                      $t(
                        'If enabled, the system will fetch and compare real-time Airbnb prices for the selected dates and show a comparison to your rates.'
                      )
                    "
                  />
                </div>

                <v-text-field
                  v-model.number="formData.custom_markup"
                  dense
                  outlined
                  type="number"
                  min="-100"
                  max="100"
                  :label="$t('Markup/Discount (%)')"
                  @input="updateForm"
                />
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined class="pa-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Cancellation Policy') }}
                </v-card-title>

                <v-textarea
                  v-model="formData.cancellation_policy"
                  dense
                  outlined
                  :label="$t('Cancellation Policy')"
                  @input="updateForm"
                />
              </v-card>

              <v-card outlined class="pa-4 mt-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Custom Terms of Use') }}
                </v-card-title>

                <text-editor
                  v-model="formData.custom_terms_of_use"
                  autofocus
                  :show-save-button="false"
                  @input="updateForm"
                />
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import FormRulesMixin from 'components/mixins/form-rules-mixin'
import TextEditor from 'components/common/text-editor'
import InfoTooltip from 'components/common/info-tooltip.vue'

export default {
  name: 'BookingSettings',

  components: {
    TextEditor,
    InfoTooltip,
  },

  mixins: [FormRulesMixin],

  props: {
    bookingEngineSetting: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      formData: {
        min_ahead: this.bookingEngineSetting.min_ahead || 0,
        default_sort: this.bookingEngineSetting.default_sort || 'reviews_desc',
        show_infants: this.bookingEngineSetting.show_infants || false,
        allow_bank_payments:
          this.bookingEngineSetting.allow_bank_payments || false,
        include_cleaning_in_nightly_rate:
          this.bookingEngineSetting.include_cleaning_in_nightly_rate || false,
        compare_prices: this.bookingEngineSetting.compare_prices || false,
        custom_markup: this.bookingEngineSetting.custom_markup || '',
        cancellation_policy:
          this.bookingEngineSetting.cancellation_policy || '',
        custom_terms_of_use:
          this.bookingEngineSetting.custom_terms_of_use || '',
        default_rate_plan_title:
          this.bookingEngineSetting.default_rate_plan_title || '',
      },
      sortOptions: [
        { text: this.$t('Reviews (High to Low)'), value: 'reviews_desc' },
        { text: this.$t('Price (Low to High)'), value: 'price_asc' },
        { text: this.$t('Price (High to Low)'), value: 'price_desc' },
      ],
    }
  },

  methods: {
    minZero(v) {
      if (!v) return true
      return v >= 0 || 'Must be 0 or greater'
    },

    maxHundred(v) {
      if (!v) return true
      return v <= 100 || 'Must be 100 or less'
    },

    updateForm() {
      if (this.$refs.form.validate()) {
        this.$emit('update:setting', this.formData)
      }
    },

    validate() {
      return this.$refs.form.validate()
    },
  },
}
</script>
